/** A small info control that opens a few sentences beside a label - ADR-0212 decision 2.
 *
 * It takes a **key into `shell/infoText.ts`**, never text: a screen cannot put a paragraph in a
 * component and call it a popover. The control is a button (`aria-haspopup`, `aria-expanded`) whose
 * accessible name is "About {title}". Opened, the text is a non-modal dialog placed beside the
 * button; focus moves into it so a screen reader reads it, and it closes on Escape (focus returns to
 * the button), on a press outside, and on Tab (focus returns to the button and Tab carries on from
 * there, because the card is portalled to the end of the document and would otherwise strand it).
 *
 * Closed, the text is not in the document at all, which is how the text-budget guard does not count
 * it as body text; it is still reachable by everyone, from the control that names it.
 */

import { Info } from "@phosphor-icons/react";
import type { KeyboardEvent } from "react";
import { useEffect, useId, useRef, useState } from "react";

import type { InfoKey } from "../../shell/infoText";
import { infoEntry } from "../../shell/infoText";
import { PreviewCard } from "./HoverCard";
import styles from "./InfoPopover.module.css";
import { usePopup } from "./usePopup";

export function InfoPopover({ infoKey, label }: { infoKey: InfoKey; label?: string }) {
  const entry = infoEntry(infoKey);
  const { open, setOpen, close, triggerRef, popupRef } = usePopup<HTMLButtonElement>();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    if (open) bodyRef.current?.focus();
  }, [open]);

  const toggle = () => {
    if (open) {
      close(false);
      return;
    }
    setAnchor(triggerRef.current?.getBoundingClientRect() ?? null);
    setOpen(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape" && open) {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab" && open) {
      close();
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        aria-label={label ?? `About ${entry.title}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        data-info-key={infoKey}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <Info size={14} weight="bold" aria-hidden="true" />
      </button>
      {open && anchor ? (
        <PreviewCard
          anchor={anchor}
          id={id}
          role="dialog"
          ariaLabel={entry.title}
          testId="info-popover"
          className={styles.card}
          popupRef={popupRef}
          onKeyDown={onKeyDown}
          align="end"
        >
          <div ref={bodyRef} tabIndex={-1} className={styles.body}>
            <p className={styles.title}>{entry.title}</p>
            {entry.body.map((paragraph) => (
              <p key={paragraph} className={styles.text}>
                {paragraph}
              </p>
            ))}
          </div>
        </PreviewCard>
      ) : null}
    </>
  );
}
